/**
 * onboardingFlowStore — Zustand store for live OnboardingFlow snapshots (C1).
 *
 * O motor empurra o frame `onboarding_flow_state_update` (engine
 * `src/services/onboarding/flow-broadcaster.ts`) com a máquina do import de
 * onboarding e o `connectionStore` roteia aqui (com guarda de tenant).
 * `OnboardingUploadCard` lê o snapshot vivo pelo `flowId` para saber se o
 * convite de import já avançou.
 *
 * STO-6650 (29/09): a etapa `review` (totais `assetsFound`/`systemsFound`)
 * tinha um segundo consumidor, `OnboardingReviewCard`, que desenhava a caixa
 * "Encontrei N ativos em M sistemas" — duplicando o mesmo fato que já vem na
 * mensagem de ações do motor. Regra anti-redundância do Denis: removido.
 *
 * Espelha o padrão do `equipmentRegistrationFlowStore`: fonte única de verdade
 * chaveada por `flowId`, sem conhecer a ordem das etapas (isso mora no motor).
 * Aqui a semântica é "último a chegar vale" — o motor emite UM frame por
 * transição. Sem ranking de etapa (o bug RAIZ 3 do store de cadastro) e sem
 * `revision`: não há mensagem-semente concorrente para o onboarding, então não
 * há corrida a arbitrar.
 */
import { create } from 'zustand'
import type {
  OnboardingFlowSnapshot,
  OnboardingFlowStateUpdateEvent,
} from '../types/rich-content.js'

interface OnboardingFlowStore {
  /** Snapshots keyed by flowId. */
  flows: Record<string, OnboardingFlowSnapshot>

  /**
   * Apply an engine-pushed state update over the WebSocket. Último a chegar
   * vale — o motor emite um frame por transição, em ordem.
   */
  applyStateUpdate: (event: OnboardingFlowStateUpdateEvent) => void

  /** Read a single snapshot by flowId. */
  getSnapshot: (flowId: string) => OnboardingFlowSnapshot | undefined

  /** Drop a snapshot from the store. */
  clear: (flowId: string) => void
}

export const useOnboardingFlowStore = create<OnboardingFlowStore>((set, get) => ({
  flows: {},

  applyStateUpdate: (event) => {
    const snapshot: OnboardingFlowSnapshot = {
      flowId: event.flowId,
      conversationId: event.conversationId,
      state: event.state,
      context: event.context,
    }
    set((state) => ({ flows: { ...state.flows, [event.flowId]: snapshot } }))
  },

  getSnapshot: (flowId) => get().flows[flowId],

  clear: (flowId) => {
    set((state) => {
      const next = { ...state.flows }
      delete next[flowId]
      return { flows: next }
    })
  },
}))
